import { useMemo, useState } from 'react';
import type { MeetingOccurrenceInput, RoomCalendarBooking, RoomCalendarRoom } from '../types';
import { bookingOverlapsMinutes, bookingsForDate } from '../lib/roomCalendar';
import { minutesFromTime } from '../lib/meetingSeries';
import { formatDate, formatMalaysiaIsoTime, formatTimeRange } from '../lib/utils';
import BookingDetailContent from './BookingDetailContent';
import Modal from './Modal';
import RoomDayTimeline from './RoomDayTimeline';
import Spinner from './Spinner';
import { sortRoomsForDisplay } from '../lib/roomOrder';

type AvailabilityStatus = 'idle' | 'loading' | 'ready' | 'error';

function roomPlace(room: RoomCalendarRoom): string {
  return [room.floor_label, room.building].filter(Boolean).join(' · ');
}

function conflictsFor(room: RoomCalendarRoom, occurrence: MeetingOccurrenceInput): RoomCalendarBooking[] {
  const start = minutesFromTime(occurrence.startTime);
  const end = minutesFromTime(occurrence.endTime);
  if (start === null || end === null) return [];
  return room.bookings.filter((booking) => bookingOverlapsMinutes(booking, occurrence.date, start, end));
}

export default function RoomAvailabilityPicker({
  rooms,
  occurrences,
  target,
  selectedRoomId,
  status,
  onSelect,
  onClose,
  onRetry,
}: {
  rooms: RoomCalendarRoom[];
  occurrences: MeetingOccurrenceInput[];
  target: Pick<MeetingOccurrenceInput, 'date' | 'startTime' | 'endTime'>;
  selectedRoomId: string | null;
  status: AvailabilityStatus;
  onSelect: (roomId: string) => void;
  onClose: () => void;
  onRetry: () => void;
}) {
  const [query, setQuery] = useState('');
  const [floor, setFloor] = useState('');
  const [bookingDetail, setBookingDetail] = useState<{ booking: RoomCalendarBooking; room: RoomCalendarRoom } | null>(null);
  const targetOccurrence: MeetingOccurrenceInput = { ...target, rooms: [] };
  const targetAvailable = (room: RoomCalendarRoom) => conflictsFor(room, targetOccurrence).length === 0;
  const availableCount = rooms.filter(targetAvailable).length;
  const floors = useMemo(() => Array.from(new Set(rooms.map(roomPlace))), [rooms]);
  const filteredRooms = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sortRoomsForDisplay(rooms).filter((room) => (!floor || roomPlace(room) === floor)
      && (!normalizedQuery || [room.name, room.building, room.floor_label, ...room.facilities]
        .filter(Boolean).join(' ').toLowerCase().includes(normalizedQuery)));
  }, [floor, query, rooms]);

  const closePicker = () => {
    setBookingDetail(null);
    onClose();
  };

  if (status === 'idle') return <Modal title="Pilih bilik mesyuarat" onClose={closePicker} size="wide"><div className="room-availability-message"><p>Pilih tarikh dan waktu mesyuarat yang sah sebelum mencari bilik.</p></div></Modal>;
  if (status === 'loading') return <Modal title="Pilih bilik mesyuarat" onClose={closePicker} size="wide"><div className="room-finder-loading"><Spinner label="Sedang menyemak jadual bilik…" /></div></Modal>;
  if (status === 'error') return <Modal title="Pilih bilik mesyuarat" onClose={closePicker} size="wide"><div className="room-availability-message" role="alert"><p>Jadual bilik tidak dapat dimuatkan. Sila cuba lagi.</p><button type="button" className="button button-ghost button-small" onClick={onRetry}>Cuba lagi</button></div></Modal>;
  if (!rooms.length) return <Modal title="Pilih bilik mesyuarat" onClose={closePicker} size="wide"><div className="room-availability-message"><p>Tiada bilik mesyuarat aktif dikonfigurasikan.</p></div></Modal>;

  return <Modal title="Pilih bilik mesyuarat" onClose={closePicker} size="wide">
      <div className="room-chooser">
        <div className="room-chooser-toolbar">
          <p className="room-chooser-time">Pilihan untuk <strong>{formatDate(target.date)} · {formatTimeRange(target.startTime, target.endTime)}</strong></p>
          <div className="room-chooser-filters">
            <label className="field"><span>Cari bilik</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nama bilik atau kemudahan" autoFocus /></label>
            {floors.length > 1 ? <label className="field"><span>Aras / bangunan</span><select value={floor} onChange={(event) => setFloor(event.target.value)}><option value="">Semua aras</option>{floors.map((label) => <option key={label} value={label}>{label}</option>)}</select></label> : null}
          </div>
          <p className="room-chooser-count" role="status">{availableCount} bilik tersedia untuk waktu dipilih · semua bilik dipaparkan</p>
          {bookingDetail ? <section className="room-picker-booking-detail" aria-label="Butiran mesyuarat ditempah">
            <BookingDetailContent booking={bookingDetail.booking} roomName={bookingDetail.room.name} />
            <button type="button" className="button button-ghost button-small" onClick={() => setBookingDetail(null)}>Tutup butiran</button>
          </section> : null}
        </div>
        <div className="multi-day-room-list" aria-label="Hasil carian bilik">
          {filteredRooms.map((room) => {
            const available = targetAvailable(room);
            const selected = room.room_id === selectedRoomId;
            return <article className={`multi-day-room-row${available ? '' : ' unavailable'}${selected ? ' selected' : ''}`} key={room.room_id}>
              <div className="multi-day-room-head">
                <div><strong>{room.name}</strong><span>{roomPlace(room)} · {room.capacity} orang</span></div>
                <div><span className={`room-status ${available ? 'available' : 'unavailable'}`}>{available ? 'Tersedia' : 'Bertindih'}</span>{available ? <button type="button" className="button button-primary button-small" onClick={() => onSelect(room.room_id)}>{selected ? 'Dipilih' : 'Pilih'}</button> : null}</div>
              </div>
              <div className="multi-day-room-days">
                {occurrences.map((occurrence) => {
                  const checkedOccurrence = occurrence.date === target.date ? targetOccurrence : occurrence;
                  const conflicts = conflictsFor(room, checkedOccurrence);
                  const hasConflict = conflicts.length > 0;
                  return <div className={`multi-day-room-day${hasConflict ? ' conflict' : ''}`} key={occurrence.date}>
                    <div><strong>{formatDate(occurrence.date)}</strong><span>{hasConflict ? `Bertindih: ${conflicts.map((booking) => `${formatMalaysiaIsoTime(booking.starts_at)}–${formatMalaysiaIsoTime(booking.ends_at)}`).join(', ')}` : `Tersedia untuk ${formatTimeRange(checkedOccurrence.startTime, checkedOccurrence.endTime)}`}</span></div>
                    <RoomDayTimeline bookings={bookingsForDate(room.bookings, occurrence.date)} date={occurrence.date} compact label={`${room.name} pada ${formatDate(occurrence.date)}`} onBookingClick={(booking) => setBookingDetail({ booking, room })} />
                  </div>;
                })}
              </div>
            </article>;
          })}
          {!filteredRooms.length ? <p className="room-filter-empty">Tiada bilik yang sepadan dengan tapisan ini.</p> : null}
        </div>
      </div>
    </Modal>;
}
